import { formatDateRange } from '@/utils/dateFormatters'
import type { OrderOverviewRow, OrderPerspectiveView } from './queries'

export type DataPerspective =
  | 'internal_order'
  | 'gravitate_order'
  | 'partner_order'

/**
 * One row as the grid renders it: the chosen perspective, plus the values
 * that sit at the overview row's top level and are shared by all three
 * perspectives -- the log behind the status chip, the group it belongs to,
 * the integration checkpoints, and the order's flags.
 */
export type OrderGridRow = Omit<OrderPerspectiveView, 'checkpoints'> &
  Pick<OrderOverviewRow, 'log' | 'log_group_id' | 'checkpoints' | 'flags'>

/**
 * Flattens an overview row for the grid. Always returns a row -- a missing
 * perspective falls back to the internal one (see getPerspectiveView) rather
 * than dropping out, because the server counted it: silently discarding rows
 * here would leave a page shorter than the total says it is.
 */
export function toGridRow(
  row: OrderOverviewRow,
  perspective: DataPerspective,
): OrderGridRow {
  return {
    ...getPerspectiveView(row, perspective),
    log: row.log,
    log_group_id: row.log_group_id,
    checkpoints: row.checkpoints,
    flags: row.flags,
  }
}

/**
 * Get the appropriate perspective view from a row based on data format selection
 */
function getPerspectiveView(
  row: OrderOverviewRow,
  perspective: DataPerspective,
): OrderPerspectiveView {
  switch (perspective) {
    case 'gravitate_order':
      return row.gravitate_order ?? row.internal_order
    case 'partner_order':
      return row.partner_order ?? row.internal_order
    case 'internal_order':
    default:
      return row.internal_order
  }
}

/**
 * Format a date range for display
 * Returns format: "Mon, 1/12, 6:00 AM - Mon, 1/12, 3:00 PM"
 */
export function formatDeliveryWindow(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  return formatDateRange(start, end)
}
